import { Stack, Tooltip, Typography, Button, Box } from '@mui/material';
import { useEffect, useState } from 'react';

import { TableIconButton } from '../../../components/Styled';
import Icon from '../../../components/FormControl/Icon';
import styles from './header.module.scss';

import { MOBILE_URL_KEY, PAIR_BASE_URL, QR_KEY, SESSION_KEY, useSAWS } from 'contexts/SAWSContext';
import { useAppContext } from 'contexts/AppContext';
import InteractiveAlertDialog from 'components/Dialoges/InteractiveAlertDialog';

const CODE_KEY = 'ros_ws_code';

const TabConnect = () => {
  const { connect, connected, onData, getSession, clearAll } = useSAWS();

  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);

  const { snack } = useAppContext();

  const restoreQR = () => {
    const qr = localStorage.getItem(QR_KEY);
    const mobile = localStorage.getItem(MOBILE_URL_KEY);
    const savedCode = localStorage.getItem(CODE_KEY);

    if (qr && mobile) {
      setQrUrl(qr);
      setCode(savedCode);
      return true;
    }

    return false;
  };

  const createNewSession = async () => {
    const res = await fetch(`${PAIR_BASE_URL}/pair/session`, { method: 'POST' });
    const data = await res.json();

    localStorage.setItem(SESSION_KEY, data.wsUrlForWeb);
    localStorage.setItem(QR_KEY, data.qrUrl);
    localStorage.setItem(MOBILE_URL_KEY, data.wsUrlForMobile);
    if (data.code) localStorage.setItem(CODE_KEY, data.code);

    connect(data.wsUrlForWeb);

    setQrUrl(data.qrUrl);
    setCode(data.code ?? null);
    setShowQr(true);
  };

  const handleReconnect = async () => {
    try {
      clearAll();
      localStorage.removeItem(CODE_KEY);
      await createNewSession();
      snack.show('New session created. Scan the QR or enter the code again.', 'info');
    } catch (err) {
      console.error(err);
      snack.show('Failed to reconnect', 'error');
    }
  };

  const handleConnectClick = async () => {
    if (connected) {
      const restored = restoreQR();
      if (restored) {
        setShowQr(true);
        snack.show('Already connected. Showing QR again.', 'info');
      } else {
        snack.show('Already connected to Store Assistant', 'info');
      }
      return;
    }

    try {
      const saved = getSession();
      if (saved) {
        connect(saved);
        snack.show('Reconnecting to Store Assistant…', 'info');
        if (restoreQR()) setShowQr(true);
        return;
      }
      await createNewSession();
      snack.show('Scan the QR or enter the code on Store Assistant', 'info');
    } catch (err) {
      console.error(err);
      snack.show('Failed to connect', 'error');
    }
  };

  const copyCode = () => {
    if (!code) return;
    navigator.clipboard?.writeText(code).then(
      () => snack.show('Code copied', 'success'),
      () => snack.show(code, 'info'),
    );
  };

  useEffect(() => {
    restoreQR();
  }, []);

  useEffect(() => {
    const off = onData((msg) => {
      try {
        const data = JSON.parse(msg);
        if (data.type === 'status' && data.event === 'paired') {
          setShowQr(false);
          snack.show('Store Assistant connected', 'success');
        }
      } catch (e) {
        console.error('Failed to parse SAWS message', e);
      }
    });
    return off;
  }, [onData]);

  useEffect(() => {
    if (connected) snack.show('Connected to Store Assistant', 'info');
  }, [connected]);

  return (
    <>
      <Tooltip title='Connect Customer Tab' placement='left'>
        <TableIconButton onClick={handleConnectClick}>
          <Icon icon='ri-link' size='1.2rem' className={styles.helpIcon} />
        </TableIconButton>
      </Tooltip>

      <InteractiveAlertDialog
        open={showQr}
        title='Connect Customer Tab'
        Body={() => (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            alignItems='center'
            sx={{ p: 3, minWidth: { sm: 520 } }}
          >
            {/* Left: instructions + code */}
            <Stack spacing={2} flex={1} sx={{ width: '100%' }}>
              <Box>
                <Typography variant='h6' fontWeight={700} color='text.primary'>
                  Pair your Store Assistant
                </Typography>
                <Typography variant='body2' color='text.secondary' mt={0.5}>
                  Scan the QR on the tablet, or enter this code in the app.
                </Typography>
              </Box>

              {code && (
                <Box
                  sx={{
                    borderRadius: 3,
                    p: 2,
                    background: 'linear-gradient(135deg,#eef2ff,#f5f3ff)',
                    border: '1px solid #e3e8ff',
                  }}
                >
                  <Typography variant='caption' color='text.secondary' letterSpacing={1}>
                    PAIRING CODE
                  </Typography>
                  <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: 34,
                        fontWeight: 700,
                        letterSpacing: 8,
                        color: '#3b3fae',
                      }}
                    >
                      {code}
                    </Typography>
                    <Button size='small' variant='text' onClick={copyCode}>
                      Copy
                    </Button>
                  </Stack>
                </Box>
              )}

              <Button
                variant='outlined'
                color='primary'
                onClick={handleReconnect}
                sx={{ alignSelf: 'flex-start', borderRadius: 2 }}
              >
                Generate new code
              </Button>
            </Stack>

            {/* Right: QR */}
            {qrUrl && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 4,
                  background: '#fff',
                  boxShadow: '0 8px 30px rgba(59,63,174,0.15)',
                  border: '1px solid #eee',
                }}
              >
                <img
                  src={qrUrl}
                  alt='Scan to connect Store Assistant'
                  style={{ width: 200, height: 200, display: 'block', borderRadius: 8 }}
                />
              </Box>
            )}
          </Stack>
        )}
        actions={[
          {
            label: 'Close',
            color: 'error',
            handler: () => setShowQr(false),
          },
        ]}
        onClose={() => setShowQr(false)}
      />
    </>
  );
};

export default TabConnect;
